import React from 'react';
import { ScrollView, View } from 'react-native'

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Toggle from '../../../../components/Toggle';
import Header from '../../../../navigation/Header';
import useCommunitySettings from '../../../../hooks/useCommunitySettings';


/** Card con borde. Opcionalmente título (con toggle propio) + línea divisora. */
function SectionCard({ title, titleValue, onTitleChange, highlight, disabled, children }: any) {
  return (
    <View style={tw.style(`bg-white rounded-2xl border border-gray-200 px-4 py-4 mb-4`, highlight && `border-primary`)}>
      {!!title && (
        <>
          <View style={tw`row items-center justify-between`}>
            <Text style={tw`text-secondary font-600 text-base flex-1 mr-3`}>{title}</Text>
            {onTitleChange && (
              <Toggle value={titleValue} onValueChange={onTitleChange} disabled={disabled} />
            )}
          </View>
          <View style={tw`border-b border-gray-200 -mx-4 mt-3 mb-4`} />
        </>
      )}
      {children}
    </View>
  )
}

function ToggleItem({ title, description, value, onValueChange, disabled, first }: any) {
  return (
    <View style={tw.style(!first && `mt-4`)}>
      <View style={tw`row items-center justify-between`}>
        <Text style={tw`text-secondary font-600 text-base flex-1 mr-3`}>{title}</Text>
        <Toggle value={value} onValueChange={onValueChange} disabled={disabled} />
      </View>
      {!!description && <Text style={tw`text-gray-500 text-sm mt-1`}>{description}</Text>}
    </View>
  )
}


export default function VigilanciaSettingsScreen() {
  const { updateSettings, getSettingValue, isUpdating } = useCommunitySettings()

  const get = (key: string, def: any) => getSettingValue(key, def)
  const set = (key: string) => (v: boolean) => updateSettings(key, v)

  return (
    <View style={tw`flex-1 bg-[#F3F2F3]`}>
      <Header showBackButton title="Configuración App de Vigilancia" style="bg-transparent" />

      <ScrollView style={tw`flex-1`} contentContainerStyle={tw`px-4 pt-4 pb-10`}>
        {/* Habilitar */}
        <SectionCard highlight>
          <ToggleItem
            first
            title="Habilitar App de Vigilancia"
            description="Habilita el uso de la aplicación móvil para guardias, permitiendo el escaneo de QRs y el registro de visitas."
            value={get('guard.app_enabled', true)}
            onValueChange={set('guard.app_enabled')}
            disabled={isUpdating}
          />
        </SectionCard>

        {/* Bitácoras */}
        <SectionCard title="Bitácoras">
          <ToggleItem
            first
            title="Residentes"
            description="El vigilante puede ver las entradas y salidas de los residentes con tag o dispositivos de identificación."
            value={get('guard.log_residents', false)}
            onValueChange={set('guard.log_residents')}
            disabled={isUpdating}
          />
          <ToggleItem
            title="Visitantes"
            description="El vigilante puede ver las entradas y salidas visitas con código QR y, registradas en caseta."
            value={get('guard.log_visitors', true)}
            onValueChange={set('guard.log_visitors')}
            disabled={isUpdating}
          />
        </SectionCard>

        {/* Registro de ingreso (Vehicular) */}
        <SectionCard title="Registro de ingreso (Vehicular)">
          <Text style={tw`text-gray-500 text-sm mb-4`}>
            Elige la información que será obligatoria para registrar un ingreso:
          </Text>

          <ToggleItem
            first
            title="Tipo de visitante"
            description="Familiar, amistad, servicio o entrega."
            value={get('guard.vehicle.visitor_type_required', true)}
            onValueChange={set('guard.vehicle.visitor_type_required')}
            disabled={isUpdating}
          />
          <ToggleItem
            title="Marca y modelo"
            description="Se necesitará proporcionar dos palabras de al menos dos caracteres cada una."
            value={get('guard.vehicle.brand_required', true)}
            onValueChange={set('guard.vehicle.brand_required')}
            disabled={isUpdating}
          />
          <ToggleItem
            title="Matrícula"
            description="6 caracteres entre números y letras."
            value={get('guard.vehicle.plate_required', true)}
            onValueChange={set('guard.vehicle.plate_required')}
            disabled={isUpdating}
          />
          <ToggleItem
            title="Color del vehículo"
            value={get('guard.vehicle.color_required', true)}
            onValueChange={set('guard.vehicle.color_required')}
            disabled={isUpdating}
          />
          <ToggleItem
            title="Evidencia fotográfica"
            description="El vigilante deberá tomar fotos del vehículo que ingresa."
            value={get('guard.vehicle.photo_required', true)}
            onValueChange={set('guard.vehicle.photo_required')}
            disabled={isUpdating}
          />
        </SectionCard>

        {/* Registro Peatonal (con toggle en el encabezado) */}
        <SectionCard
          title="Registro Peatonal"
          titleValue={get('guard.pedestrian.enabled', true)}
          onTitleChange={set('guard.pedestrian.enabled')}
          disabled={isUpdating}
        >
          <ToggleItem
            first
            title="Tipo de visitante"
            description="Familiar, amistad, servicio o entrega."
            value={get('guard.pedestrian.visitor_type_required', true)}
            onValueChange={set('guard.pedestrian.visitor_type_required')}
            disabled={isUpdating}
          />
          <ToggleItem
            title="Fotografía"
            description="Tomar fotografía de identificación."
            value={get('guard.pedestrian.photo_required', true)}
            onValueChange={set('guard.pedestrian.photo_required')}
            disabled={isUpdating}
          />
        </SectionCard>

        {/* Nota informativa (sin card) */}
        <View style={tw`px-1 mb-4`}>
          <Text style={tw`text-gray-500 text-sm`}>
            En todo registro de accesos será obligatorio ingresar la{' '}
            <Text style={tw`text-gray-700 font-600`}>Dirección a la que se dirige</Text> y el{' '}
            <Text style={tw`text-gray-700 font-600`}>Nombre del visitante</Text>.
          </Text>
          <Text style={tw`text-gray-500 text-sm mt-2`}>
            Habrá un campo opcional de comentarios adicionales.
          </Text>
        </View>

        {/* Autorización de acceso */}
        <SectionCard>
          <ToggleItem
            first
            title="Autorización de acceso"
            description="El vigilante puede registrar autorizaciones de proveedores que los residentes soliciten por llamada telefónica."
            value={get('guard.access_authorization', true)}
            onValueChange={set('guard.access_authorization')}
            disabled={isUpdating}
          />
        </SectionCard>

        {/* Reparto en caseta */}
        <SectionCard>
          <ToggleItem
            first
            title="Reparto en caseta"
            description="Los vigilantes pueden recibir repartos y almacenarlos en caseta."
            value={get('guard.booth_delivery', true)}
            onValueChange={set('guard.booth_delivery')}
            disabled={isUpdating}
          />
          <ToggleItem
            title="Foto obligatoria"
            description="Para marcar como “Entregado”, se debe tomar una fotografía de quien recibe el paquete."
            value={get('guard.booth_delivery_photo_required', true)}
            onValueChange={set('guard.booth_delivery_photo_required')}
            disabled={isUpdating}
          />
        </SectionCard>
      </ScrollView>
    </View>
  );
}
